import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, ArrowRight, AlertCircle, Mail, X, BookHeart } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import Button from '../ui/Button';
import GoogleIcon from './GoogleIcon';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

interface RegisterFormProps {
  formData: {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName1: string;
    lastName2: string;
  };
  errors: Record<string, string>;
  loading: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordStrength: PasswordStrength | null;
  isInvitationFlow: boolean;
  emailInputRef: React.RefObject<HTMLInputElement>;
  policiesAccepted: boolean;
  setPoliciesAccepted: (v: boolean) => void;
  sensitiveDataAccepted: boolean;
  setSensitiveDataAccepted: (v: boolean) => void;
  onValidateConsent: () => boolean;
  onFieldChange: (field: string, value: string) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitchMode: (mode: 'login' | 'register' | 'forgot-password') => void;
  onGoogleSignIn: (code: string) => void;
  onClose?: () => void;
}

const ROTATING_WORDS = [
  { word: 'madre', fontClass: 'font-hand-madre' },
  { word: 'padre', fontClass: 'font-hand-padre' },
  { word: 'abuela', fontClass: 'font-hand-abuela' },
  { word: 'abuelo', fontClass: 'font-hand-abuelo' },
];

const RotatingWord: React.FC<{ variant: 'mobile' | 'desktop' }> = ({ variant }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % ROTATING_WORDS.length), 2500);
    return () => clearInterval(id);
  }, []);
  const { word, fontClass } = ROTATING_WORDS[idx];
  const sizeClass = variant === 'mobile' ? 'text-[28px]' : 'text-[48px]';
  // Invisible-spacer + absolutely-positioned active word prevents layout shift
  // as the font/word changes. Same technique as the landing hero (HeroSection.tsx).
  // Spacer uses the widest combo ("abuela" in Permanent Marker) to reserve
  // constant width; the active word sits absolutely inside and doesn't reflow.
  return (
    <span className="relative inline-block align-baseline pr-1" aria-hidden="true">
      <span className={`invisible font-hand-padre ${sizeClass} select-none`} aria-hidden="true">
        abuela
      </span>
      <span className={`${fontClass} text-rose-500 ${sizeClass} absolute left-0 bottom-0 whitespace-nowrap`}>
        {word}
      </span>
    </span>
  );
};

const RegisterForm: React.FC<RegisterFormProps> = ({
  formData,
  errors,
  loading,
  showPassword,
  showConfirmPassword,
  passwordStrength,
  isInvitationFlow,
  emailInputRef,
  policiesAccepted,
  setPoliciesAccepted,
  sensitiveDataAccepted,
  setSensitiveDataAccepted,
  onValidateConsent,
  onFieldChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onSubmit,
  onSwitchMode,
  onGoogleSignIn,
  onClose
}) => {
  const consentError = errors.consent;
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    onSuccess: (response) => onGoogleSignIn(response.code),
    onError: () => onGoogleSignIn(''),
  });

  // Intercept the Google button click: if consent is missing, surface the error
  // and do NOT start the Google OAuth popup (prevents orphan auth users).
  const handleGoogleClick = () => {
    if (!onValidateConsent()) return;
    googleLogin();
  };

  const inputBase =
    'w-full border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 placeholder:text-stone-400 transition-all duration-200';
  const inputSizing = 'h-9 text-sm px-3 lg:h-11 lg:px-3.5';
  const inputClass = (hasError: boolean) =>
    `${inputBase} ${inputSizing} ${hasError ? 'border-rose-500' : 'border-stone-200'}`;

  const checkboxError = consentError ? 'ring-2 ring-rose-300' : '';

  return (
    <form onSubmit={onSubmit} className="h-full w-full flex flex-col lg:flex-row min-h-0">
      {/* MOBILE: compact gradient header */}
      <div className="lg:hidden warm-panel relative pt-3 pb-2 px-5 shrink-0">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="absolute right-3 top-3 flex items-center justify-center text-stone-500 rounded-full bg-white/60 hover:bg-white/80 transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        )}
        <h2
          id="auth-modal-title"
          className="font-display text-[22px] leading-[1.1] text-stone-900 pr-10"
        >
          Empieza tu proyecto
        </h2>
        <p className="text-[12px] text-stone-600 mt-0.5">
          Crea tu cuenta — comienza en minutos
        </p>
      </div>

      {/* DESKTOP: split-panel left — brand hero */}
      <div className="hidden lg:flex lg:w-1/2 warm-panel relative overflow-hidden flex-col justify-center px-14">
        <div className="w-10 h-10 bg-rose-400 rounded-2xl flex items-center justify-center text-white shadow-lg mb-4">
          <BookHeart size={20} />
        </div>
        <h1 className="font-display text-[40px] leading-[1.15] text-stone-900 tracking-tight">
          Escribe la historia de{' '}
          <span className="whitespace-nowrap">
            tu <RotatingWord variant="desktop" />
          </span>
        </h1>
        <p className="text-sm text-stone-600 mt-5 max-w-[360px] leading-relaxed">
          Descubre las historias que nunca te contaron. 42 preguntas guiadas, conversaciones reales, un libro para tu familia.
        </p>
        <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-rose-200/40 blur-3xl pointer-events-none" />
      </div>

      {/* FORM REGION — mobile: sits on gradient with floating white card.
          Desktop: right half, bg-[#FEFCFA], plain direct fields. */}
      <div className="warm-panel lg:w-1/2 lg:bg-[#FEFCFA] flex-1 flex flex-col min-h-0 lg:px-12 lg:py-10 relative">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="hidden lg:flex items-center justify-center absolute top-10 right-10 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-full transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        )}

        {/* Desktop-only title */}
        <div className="hidden lg:block mb-4 shrink-0">
          <h2 className="text-xl font-semibold text-stone-900 mb-0.5">Crear cuenta</h2>
          <p className="text-xs text-stone-500">Menos de un minuto</p>
        </div>

        {/* Mobile card wrapper / desktop plain */}
        <div className="flex-1 flex flex-col min-h-0 px-3 pb-3 lg:px-0 lg:pb-0">
          <div className="flex-1 flex flex-col min-h-0 bg-white lg:bg-transparent rounded-2xl lg:rounded-none border lg:border-0 border-stone-200 shadow-sm lg:shadow-none p-3 lg:p-0">

            {isInvitationFlow && (
              <div className="mb-2 p-2 bg-rose-50 border border-rose-200 rounded-lg shrink-0">
                <p className="text-[12px] lg:text-xs text-rose-900">
                  ¿Ya tienes una cuenta con este correo?{' '}
                  <button
                    type="button"
                    onClick={() => onSwitchMode('login')}
                    className="font-semibold text-rose-700 hover:text-rose-800 underline"
                  >
                    Inicia sesión
                  </button>
                </p>
              </div>
            )}

            <div className="space-y-2 lg:space-y-2.5 flex-1 min-h-0 overflow-y-auto p-0.5">
              {/* Row 1 — Nombre + Apellido */}
              <div className="grid grid-cols-2 gap-2 lg:gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-[12px] lg:text-[13px] font-medium text-stone-700 mb-0.5 lg:mb-1">
                    Nombre <span className="hidden lg:inline">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => onFieldChange('firstName', e.target.value)}
                    className={inputClass(Boolean(errors.firstName))}
                    placeholder="Juan"
                    required
                    autoComplete="given-name"
                  />
                  {errors.firstName && (
                    <p className="mt-0.5 text-[11px] text-rose-600 flex items-center" role="alert">
                      <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName1" className="block text-[12px] lg:text-[13px] font-medium text-stone-700 mb-0.5 lg:mb-1">
                    <span className="lg:hidden">Apellido</span>
                    <span className="hidden lg:inline">Primer apellido *</span>
                  </label>
                  <input
                    id="lastName1"
                    type="text"
                    value={formData.lastName1}
                    onChange={(e) => onFieldChange('lastName1', e.target.value)}
                    className={inputClass(Boolean(errors.lastName1))}
                    placeholder="Pérez"
                    required
                    autoComplete="family-name"
                  />
                  {errors.lastName1 && (
                    <p className="mt-0.5 text-[11px] text-rose-600 flex items-center" role="alert">
                      <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                      {errors.lastName1}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2 — mobile: Email only. Desktop: 2º apellido + Email */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-3">
                <div className="hidden lg:block">
                  <label htmlFor="lastName2" className="block text-[13px] font-medium text-stone-700 mb-1">
                    2º apellido
                  </label>
                  <input
                    id="lastName2"
                    type="text"
                    value={formData.lastName2}
                    onChange={(e) => onFieldChange('lastName2', e.target.value)}
                    className={inputClass(false)}
                    placeholder="(opcional)"
                    autoComplete="additional-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[12px] lg:text-[13px] font-medium text-stone-700 mb-0.5 lg:mb-1">
                    Email <span className="hidden lg:inline">*</span>
                  </label>
                  <input
                    ref={emailInputRef}
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => !isInvitationFlow && onFieldChange('email', e.target.value)}
                    readOnly={isInvitationFlow}
                    className={`${inputClass(Boolean(errors.email))} ${isInvitationFlow ? 'bg-stone-50 text-stone-500 cursor-not-allowed' : ''}`}
                    placeholder="tu@email.com"
                    required
                    autoComplete="email"
                    inputMode="email"
                  />
                  {errors.email && (
                    <p className="mt-0.5 text-[11px] text-rose-600 flex items-center" role="alert">
                      <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 3 — Password + Confirm */}
              <div className="grid grid-cols-2 gap-2 lg:gap-3">
                <div>
                  <label htmlFor="password" className="block text-[12px] lg:text-[13px] font-medium text-stone-700 mb-0.5 lg:mb-1">
                    Contraseña <span className="hidden lg:inline">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => onFieldChange('password', e.target.value)}
                      className={`${inputClass(Boolean(errors.password))} pr-9`}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={onTogglePassword}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors touch-manipulation p-1 !min-h-0 !min-w-0"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-0.5 text-[11px] text-rose-600 flex items-center" role="alert">
                      <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-[12px] lg:text-[13px] font-medium text-stone-700 mb-0.5 lg:mb-1">
                    Confirmar <span className="hidden lg:inline">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => onFieldChange('confirmPassword', e.target.value)}
                      className={`${inputClass(Boolean(errors.confirmPassword))} pr-9`}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={onToggleConfirmPassword}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors touch-manipulation p-1 !min-h-0 !min-w-0"
                      aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-0.5 text-[11px] text-rose-600 flex items-center" role="alert">
                      <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              {/* Password strength — desktop only (keeps mobile no-scroll even in error state) */}
              {formData.password && passwordStrength && (
                <div className="hidden lg:flex items-center gap-2 leading-none pt-0.5">
                  {[
                    { key: 'length', label: '8+' },
                    { key: 'uppercase', label: 'A' },
                    { key: 'lowercase', label: 'a' },
                    { key: 'number', label: '1' },
                    { key: 'special', label: '@' }
                  ].map(({ key, label }) => (
                    <span
                      key={key}
                      className={`text-[12px] font-semibold ${
                        passwordStrength.checks[key as keyof typeof passwordStrength.checks]
                          ? 'text-emerald-600'
                          : 'text-stone-300'
                      }`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {/* Submit error / success */}
              {errors.submit && (
                <div
                  className={`p-2 rounded-lg ${
                    errors.submit.startsWith('success:')
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : errors.submit.startsWith('userExists:')
                      ? 'bg-rose-50 border border-rose-200'
                      : 'bg-rose-50 text-rose-800'
                  }`}
                >
                  <div className="flex items-start gap-1.5">
                    {errors.submit.startsWith('success:') ? (
                      <Mail className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-green-600" />
                    ) : (
                      <AlertCircle
                        className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${
                          errors.submit.startsWith('userExists:') ? 'text-rose-600' : ''
                        }`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <span
                        className={`text-[12px] lg:text-xs leading-snug ${
                          errors.submit.startsWith('success:')
                            ? 'text-green-900'
                            : errors.submit.startsWith('userExists:')
                            ? 'text-rose-900'
                            : ''
                        }`}
                      >
                        {errors.submit.replace('success:', '').replace('userExists:', '')}
                      </span>
                      {errors.submit.startsWith('success:') && (
                        <Button
                          type="button"
                          onClick={() => onSwitchMode('login')}
                          className="mt-1.5 w-full bg-stone-900 hover:bg-stone-800"
                          size="sm"
                        >
                          Ya confirmé — Iniciar Sesión
                        </Button>
                      )}
                      {errors.submit.startsWith('userExists:') && (
                        <Button
                          type="button"
                          onClick={() => onSwitchMode('login')}
                          className="mt-1.5 w-full bg-stone-900 hover:bg-stone-800"
                          size="sm"
                        >
                          Ir a Iniciar Sesión
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Consent block (PRO-241) */}
              <div
                id="consent-block"
                className={`rounded-lg lg:rounded-xl transition-all ${
                  consentError
                    ? 'bg-rose-50 border border-rose-300 p-2 lg:p-3 animate-shake'
                    : 'bg-rose-50/40 border border-rose-100 p-2 lg:p-3'
                }`}
              >
                {consentError && (
                  <p
                    id="consent-error"
                    role="alert"
                    className="text-[12px] lg:text-[12px] font-semibold text-rose-700 flex items-start gap-1 mb-1 lg:mb-1.5"
                  >
                    <AlertCircle className="h-3 w-3 lg:h-3.5 lg:w-3.5 mt-0.5 shrink-0" />
                    <span>{consentError}</span>
                  </p>
                )}

                <label className="flex items-start gap-2 text-[12px] lg:text-[12px] text-stone-700 leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    checked={policiesAccepted}
                    onChange={(e) => setPoliciesAccepted(e.target.checked)}
                    disabled={loading}
                    aria-invalid={Boolean(consentError)}
                    aria-describedby={consentError ? 'consent-error' : undefined}
                    className={`mt-0.5 h-4 w-4 rounded border border-stone-300 accent-rose-500 shrink-0 focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-0 ${checkboxError}`}
                  />
                  <span>
                    Acepto los{' '}
                    <a
                      href="/terminos"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-600 hover:text-rose-700 underline"
                    >
                      Términos
                    </a>{' '}
                    y la{' '}
                    <a
                      href="/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-600 hover:text-rose-700 underline"
                    >
                      Política de privacidad
                    </a>
                    .
                  </span>
                </label>

                <label className="flex items-start gap-2 text-[12px] lg:text-[12px] text-stone-700 leading-snug cursor-pointer mt-0.5 lg:mt-1">
                  <input
                    type="checkbox"
                    checked={sensitiveDataAccepted}
                    onChange={(e) => setSensitiveDataAccepted(e.target.checked)}
                    disabled={loading}
                    aria-invalid={Boolean(consentError)}
                    aria-describedby={consentError ? 'consent-error' : undefined}
                    className={`mt-0.5 h-4 w-4 rounded border border-stone-300 accent-rose-500 shrink-0 focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-0 ${checkboxError}`}
                  />
                  <span>
                    Consiento el tratamiento de mi voz y datos sensibles (Art. 9 RGPD).
                  </span>
                </label>
              </div>
            </div>

            {/* CTAs — same layout as LoginForm: submit pill, "o" divider, full-width Google pill */}
            <div className="pt-3 space-y-2 shrink-0">
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 shadow-lg shadow-rose-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                    Crear cuenta
                  </span>
                ) : (
                  <>
                    Crear cuenta <ArrowRight size={15} />
                  </>
                )}
              </button>

              {!isInvitationFlow && (
                <>
                  <div className="flex items-center gap-3 py-0.5">
                    <div className="flex-1 h-px bg-stone-200" />
                    <span className="text-xs text-stone-400">o</span>
                    <div className="flex-1 h-px bg-stone-200" />
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleClick}
                    disabled={loading}
                    className="w-full h-10 flex items-center justify-center gap-2 border-2 border-stone-200 rounded-full hover:border-stone-300 hover:bg-stone-50 shadow-sm text-sm font-medium text-stone-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  >
                    <GoogleIcon size={18} />
                    <span>Continuar con Google</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mode switch — outside the card on mobile, below CTAs on desktop */}
          <p className="text-center text-[12px] lg:text-xs text-stone-600 lg:text-stone-500 pt-2 lg:pt-3 shrink-0">
            ¿Ya tienes cuenta?{' '}
            <button
              type="button"
              onClick={() => onSwitchMode('login')}
              className="text-rose-600 hover:text-rose-700 font-semibold"
            >
              Inicia sesión
            </button>
          </p>
        </div>
      </div>
    </form>
  );
};

export default RegisterForm;
